'use client';

/**
 * LocationSelector — buyer-side geo override for /market (S3-H7).
 * Spec: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item H-7 (HIGH).
 * CR-CHECKLIST §H (Fix de Raíz) + §I (≤200 líneas).
 *
 * ROOT CAUSE: marketplace geo filter tabs (Local/Nacional/Internacional) read
 * `filters.userCountryIso` + `filters.userCityId` but NEITHER was ever
 * populated — no UI to pick a location, /api/auth/me did not expose KYC
 * residence location. 'Local' tab was dead (TODO at market/page.tsx:465).
 *
 * FIX DE RAÍZ: THIS component IS the missing UI — "Cambiar ubicación" opens a
 * Dialog with 3 cascading dropdowns that RE-USE the existing
 * `api.geo.getCountries/getStates/getCities` endpoints (Sprint 138 PR-A.2,
 * NO parallel location system). On save, writes the localStorage key that
 * market/page.tsx reads + calls onLocationChange to re-fetch. On mount, seeds
 * parent filters from (priority): localStorage override → user.kycLocation
 * (exposed on /auth/me by Sprint 1 TODO 2) → user.pais (country-only).
 * Replaces the unreliable navigator.language fallback.
 * Anti-§6: pure UI wrapper. Same key, same endpoints, same PublicUser.
 */

import { useCallback, useEffect, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/use-auth';
import type { GeoLocationOption } from '@profileia/shared/types/geo-location';

const STORAGE_KEY = 'profileia:buyer-location';
interface BuyerLocation { countryIso: string; cityId: string; }
interface LocationSelectorProps {
  onLocationChange: (next: BuyerLocation) => void;
  currentCountryIso: string;
  currentCityId: string;
}

function readStored(): BuyerLocation | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<BuyerLocation>;
    if (typeof p?.countryIso === 'string' && typeof p?.cityId === 'string' && p.countryIso && p.cityId) {
      return { countryIso: p.countryIso, cityId: p.cityId };
    }
  } catch { /* corrupt JSON or private mode — ignore */ }
  return null;
}

function writeStored(loc: BuyerLocation): void {
  if (typeof window === 'undefined') return;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(loc)); } catch { /* private mode */ }
}

/** One of the 3 cascading dropdown rows (country / state / city). */
function GeoRow({ id, label, value, onValueChange, options, loading, disabled, placeholder }: {
  id: string; label: string; value: string;
  onValueChange: (v: string) => void;
  options: GeoLocationOption[]; loading: boolean; disabled: boolean;
  placeholder: string;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium">{label}</label>
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={loading ? 'Cargando…' : placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.geoLocationId} value={o.geoLocationId}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function LocationSelector({
  onLocationChange, currentCountryIso, currentCityId,
}: LocationSelectorProps) {
  const { user, loading: authLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState<GeoLocationOption[]>([]);
  const [states, setStates] = useState<GeoLocationOption[]>([]);
  const [cities, setCities] = useState<GeoLocationOption[]>([]);
  const [selCountry, setSelCountry] = useState('');
  const [selState, setSelState] = useState('');
  const [selCity, setSelCity] = useState('');
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);

  // Mount: seed parent filters from localStorage → /auth/me → pais.
  useEffect(() => {
    const stored = readStored();
    if (stored) { onLocationChange(stored); return; }
    if (authLoading) return;
    if (user?.kycLocation?.countryIso && user.kycLocation.cityId) {
      const loc = { countryIso: user.kycLocation.countryIso, cityId: user.kycLocation.cityId };
      writeStored(loc); onLocationChange(loc); return;
    }
    if (user?.pais) onLocationChange({ countryIso: user.pais, cityId: '' }); // country-only
  }, [authLoading, user, onLocationChange]);

  const label = currentCityId
    ? cities.find((c) => c.geoLocationId === currentCityId)?.name ?? 'Ubicación guardada'
    : currentCountryIso
      ? countries.find((c) => c.code === currentCountryIso)?.name ?? currentCountryIso
      : 'Sin ubicación';

  // Dialog open: lazy-load countries (once).
  useEffect(() => {
    if (!open || countries.length > 0) return;
    setLoadingCountries(true);
    api.geo.getCountries().then(setCountries).catch(() => setCountries([]))
      .finally(() => setLoadingCountries(false));
  }, [open, countries.length]);

  // Preselect current country when the dialog first opens.
  useEffect(() => {
    if (!open || !currentCountryIso || selCountry || countries.length === 0) return;
    const m = countries.find((c) => c.code === currentCountryIso);
    if (m) setSelCountry(m.geoLocationId);
  }, [open, currentCountryIso, selCountry, countries]);

  // Country → States (P-321: clear immediately).
  useEffect(() => {
    if (!selCountry) { setStates([]); setCities([]); setSelState(''); setSelCity(''); return; }
    setLoadingStates(true);
    setStates([]); setCities([]); setSelState(''); setSelCity('');
    api.geo.getStates(selCountry).then(setStates).catch(() => setStates([]))
      .finally(() => setLoadingStates(false));
  }, [selCountry]);

  // State → Cities.
  useEffect(() => {
    if (!selState) { setCities([]); setSelCity(''); return; }
    setLoadingCities(true);
    setCities([]); setSelCity('');
    api.geo.getCities(selState).then(setCities).catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selState]);

  const handleSave = useCallback(async () => {
    if (!selCountry || !selCity) return;
    const country = countries.find((c) => c.geoLocationId === selCountry);
    const countryIso = country?.code ?? '';
    if (!countryIso) return;
    setSaving(true);
    try {
      const loc: BuyerLocation = { countryIso, cityId: selCity };
      writeStored(loc); onLocationChange(loc); setOpen(false);
    } finally { setSaving(false); }
  }, [selCountry, selCity, countries, onLocationChange]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" aria-label="Cambiar ubicación del comprador">
          <MapPin className="size-4" aria-hidden="true" />
          <span className="max-w-[180px] truncate">{label}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tu ubicación</DialogTitle>
          <DialogDescription>
            Selecciona tu país, estado y ciudad para filtrar productos por proximidad geográfica.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <GeoRow id="ls-country" label="País" value={selCountry} onValueChange={setSelCountry}
            options={countries} loading={loadingCountries} disabled={loadingCountries}
            placeholder="Selecciona un país" />
          <GeoRow id="ls-state" label="Estado / Departamento" value={selState} onValueChange={setSelState}
            options={states} loading={loadingStates} disabled={!selCountry || loadingStates}
            placeholder={!selCountry ? 'Primero un país' : 'Selecciona'} />
          <GeoRow id="ls-city" label="Ciudad" value={selCity} onValueChange={setSelCity}
            options={cities} loading={loadingCities} disabled={!selState || loadingCities}
            placeholder={!selState ? 'Primero un estado' : 'Selecciona'} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!selCountry || !selCity || saving}>
            {saving ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LocationSelector;
